import { useModuleBEM } from "@b2bportal/core-themes";
import type { PlatformComponentProps } from "@b2bportal/core-types";
import { useI18nContext } from "@hopper-b2b/i18n";
import {
  CarPriceBreakdown,
  CarPriceDisplay,
  getVehicleClassLabelFromVehicle,
} from "@tenant/cars";
import { Card } from "@tenant/ui";
import clsx from "clsx";
import defaultStyles from "./CarShopBookingSection.module.scss";
import { CollapsiblePriceBottomSection } from "@/modules/checkout/components/CollapsiblePriceBottomSection";
import type { VehicleAvailabilityWithSupplier } from "~core-cars/store";
import { useCarsStyles } from "~core-cars/themes";
import { CoreCarsComponent } from "~core-cars/types";

export type CarShopBookingSectionType = PlatformComponentProps & {
  onClick?: () => void;
  car: VehicleAvailabilityWithSupplier;
  dayCount: number;
};
const COMPONENT_KEY = CoreCarsComponent.CarShopBookingSection;

export const CarShopBookingSection = ({
  className,
  children,
  onClick,
  car,
  dayCount,
}: CarShopBookingSectionType) => {
  const styles = useCarsStyles(COMPONENT_KEY, defaultStyles);
  const [block, cn] = useModuleBEM(styles, COMPONENT_KEY);

  const { t, formatFiatCurrency } = useI18nContext();

  const classLabel = getVehicleClassLabelFromVehicle(t, car.vehicle);

  return (
    <Card className={clsx(block, className)}>
      <div className={cn("price-breakdown")}>
        <div className={cn("full-breakdown")}>
          <p className={cn("vehicle-class")}>{classLabel}</p>
          <CarPriceDisplay
            prices={{
              total: car.totalCharge,
              perDay: car.dailyCharge,
            }}
            days={dayCount}
          />
        </div>
      </div>
      <CollapsiblePriceBottomSection
        amountDue={formatFiatCurrency(car.totalCharge.fiat)}
        priceBreakdownContent={
          <CarPriceBreakdown
            breakdown={{
              base: car.baseRate,
              payAtDesk: car.payAtDesk,
              payNow: car.payNow,
              totalCharge: car.totalCharge,
              feesSummary: {
                total: car.fees,
              },
            }}
            vehicle={car.vehicle}
          />
        }
        hidePriceBreakdown={false}
        buttons={[
          {
            label: t("continue"),
            onClick: onClick,
            disabled: onClick == null,
            props: {
              className: cn("button"),
            },
          },
        ]}
      />
      {children}
    </Card>
  );
};
